import { type RaRecord, useGetIdentity, useTranslate } from "ra-core";

import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { RelativeDate } from "../misc/RelativeDate";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ActivityCompanyNoteCreated } from "../types";
import { useActivityLogContext } from "./ActivityLogContext";
import { ActivityLogNote } from "./ActivityLogNote";
import { useIsMobile } from "@/hooks/use-mobile";

type ActivityLogCompanyNoteCreatedProps = {
  activity: RaRecord & ActivityCompanyNoteCreated;
};

// A note written about a company itself (the company page's Notes tab), beside
// the contact and deal notes the log already lists. LCCRM-87.
export function ActivityLogCompanyNoteCreated({
  activity,
}: ActivityLogCompanyNoteCreatedProps) {
  const context = useActivityLogContext();
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { companyNote } = activity;
  const isCurrentUser = activity.sales_id === identity?.id;
  const salesName = useGetSalesName(activity.sales_id, {
    enabled: !isCurrentUser,
  });
  return (
    <ActivityLogNote
      header={
        <div className="flex flex-row items-start gap-2 flex-grow">
          <ReferenceField
            source="company_id"
            reference="companies"
            record={companyNote}
            link={false}
          >
            <CompanyAvatar width={20} height={20} />
          </ReferenceField>

          <span className="text-muted-foreground text-sm flex-grow">
            {translate(
              isCurrentUser
                ? "crm.activity.you_added_note_about_company"
                : "crm.activity.added_note_about_company",
              { name: salesName },
            )}{" "}
            {/* On the company's own page the name would only repeat the title. */}
            {context !== "company" && (
              <>
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  record={companyNote}
                  link="show"
                >
                  <TextField source="name" />
                </ReferenceField>{" "}
                <RelativeDate date={activity.date} />
              </>
            )}
          </span>

          {context === "company" && (
            <span className="text-muted-foreground text-sm">
              <RelativeDate date={activity.date} />
            </span>
          )}
        </div>
      }
      text={companyNote.text}
      channelId={companyNote.channel_id}
      link={
        isMobile ? false : `/companies/${companyNote.company_id}/show/notes`
      }
    />
  );
}
